import { CheckCircleIcon, ClockIcon, LightBulbIcon } from '@heroicons/react/24/outline';
import { Trans, useTranslation } from 'react-i18next';
import RetentionOfferShell from '../RetentionOfferShell.component';
import { resolveRetentionOfferPriceView } from '../RetentionOfferModal.functions';
import { tPriceTooHighOfferModalProps } from '../RetentionOfferModal.types';

/**
 * The discount offer. Every term comes from the offer the server resolved: the
 * percent, the months and — when the server priced the subscription exactly —
 * the current and discounted amounts. Serves every reason that gets a discount;
 * `copyVariant` only picks the intro.
 */
export default function PriceTooHighOfferModal(
  props: tPriceTooHighOfferModalProps,
) {
  const {
    isOpen,
    offer,
    copyVariant,
    onAcceptOffer,
    onFinalizeSubscription,
    onBack,
    onClose,
    isLoadingOfferAcceptance,
    isLoadingFinalization,
  } = props;
  const { t, i18n } = useTranslation('settings/SubscriptionManager');

  const priceView = resolveRetentionOfferPriceView({
    offer,
    locale: i18n.language,
  });
  const terms = { percent: offer.percentOff, months: offer.durationMonths };
  const perMonth = t('page.SubscriptionPlans.perMonthly');

  return (
    <RetentionOfferShell
      isOpen={isOpen}
      // The savings total says more than the percent does, so it stays the heading
      // wherever the server priced the subscription exactly.
      heading={
        priceView
          ? t('cancelSubscription.retentionOffer.heading', {
              amount: priceView.totalSavings,
              months: offer.durationMonths,
            })
          : t('cancelSubscription.retentionOffer.headingPercent', terms)
      }
      intro={t(
        copyVariant === 'price'
          ? 'cancelSubscription.retentionOffer.intro'
          : 'cancelSubscription.retentionOffer.introNeutral',
      )}
      cardIcon={ClockIcon}
      cardTitle={t('cancelSubscription.retentionOffer.limitedTime')}
      cardTerm={t('cancelSubscription.retentionOffer.duration', {
        months: offer.durationMonths,
      })}
      footnote={
        <>
          <LightBulbIcon
            aria-hidden="true"
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-neutral-500"
          />
          {t('cancelSubscription.retentionOffer.footerMessage')}
        </>
      }
      onBack={onBack}
      onDecline={onFinalizeSubscription}
      onAccept={onAcceptOffer}
      acceptLabel={t('cancelSubscription.retentionOffer.acceptButton')}
      declineLabel={t('cancelSubscription.retentionOffer.declineButton')}
      onClose={onClose}
      isLoadingAccept={isLoadingOfferAcceptance}
      isLoadingDecline={isLoadingFinalization}
      testIdPrefix="price-too-high-offer"
    >
      {/* Prices only when the server priced the subscription exactly */}
      {priceView && (
        <div
          className="flex flex-col gap-1"
          data-testid="price-too-high-offer-price"
        >
          <p className="flex items-baseline gap-2 text-sm text-neutral-600">
            {t('cancelSubscription.retentionOffer.previousPrice')}
            <span className="text-base text-neutral-500 line-through">
              {priceView.currentAmount}/{perMonth}
            </span>
          </p>
          <p className="text-sm text-neutral-600">
            {t('cancelSubscription.retentionOffer.specialPrice')}
          </p>
          <p className="text-neutral-800">
            <span className="text-3xl font-bold">
              {priceView.offerAmount}/{perMonth}
            </span>
          </p>
        </div>
      )}

      <ul className="flex flex-col gap-2 border-t border-neutral-200 pt-4">
        <li className="flex items-start gap-2 text-base text-neutral-700">
          <CheckCircleIcon
            aria-hidden="true"
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-success"
          />
          <span>
            <Trans
              i18nKey="settings/SubscriptionManager:cancelSubscription.retentionOffer.benefit1"
              values={terms}
              components={{ bold: <strong /> }}
            />
          </span>
        </li>
        <li className="flex items-start gap-2 text-base text-neutral-700">
          <CheckCircleIcon
            aria-hidden="true"
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-success"
          />
          <span>
            <Trans
              i18nKey="settings/SubscriptionManager:cancelSubscription.retentionOffer.benefit2"
              components={{ bold: <strong /> }}
            />
          </span>
        </li>
        <li className="flex items-start gap-2 text-base text-neutral-700">
          <CheckCircleIcon
            aria-hidden="true"
            className="mt-0.5 h-5 w-5 flex-shrink-0 text-success"
          />
          <span>{t('cancelSubscription.retentionOffer.benefit3')}</span>
        </li>
      </ul>
    </RetentionOfferShell>
  );
}
